import React, { FC, useMemo, memo } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment-timezone'
import BotIcon from 'transfix-icons/build/BotIcon'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles, Box, Typography } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ShipmentIssueType } from 'apps/marketplace/apiTypes/shipmentIssues'
import ActionsMenu from 'apps/marketplace/components/ActionsMenu'
import { EditIcon, DeleteIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { useIssuePriorityLabel } from '../shared/priorityOptions'
import { MODAL_TYPE } from './ControlPanelIssueTypeModal'

interface ShipmentIssueTypeCategory extends ShipmentIssueType {
  categoryName: string | null
}

const useControlPanelRowStyles = makeStyles(() => ({
  descriptionCell: {
    maxWidth: '500px',
  },
}))

interface IControlPanelRowProps {
  issueType: ShipmentIssueTypeCategory
  onDeleteClick: (id: string | number) => void
  onEditClick: (action: keyof typeof MODAL_TYPE, id: string | number) => void
}

// TODO: SOP-2346 Remove placeholder and update with correct number
const rules = []

const ControlPanelRow: FC<IControlPanelRowProps> = memo(
  ({ issueType, onDeleteClick, onEditClick }) => {
    const isMpShipmentIssueAutomationEnabled = useFeatureFlag('mpShipmentIssueAutomation')
    const {
      categoryName,
      defaultPriority,
      deletable,
      description,
      id,
      name,
      shipmentIssueTypeDefaultOwner,
      updatedAt,
      updatedBy,
    } = issueType || {}
    const classes = useControlPanelRowStyles()
    const currentPriority = useIssuePriorityLabel(defaultPriority)

    const history = useHistory()

    const formattedUpdatedAt = updatedAt
      ? `${moment(updatedAt).format(FORMATS.dateFormat)}`
      : DISPLAY.empty

    const formattedUpdatedBy = updatedAt ? `by ${updatedBy?.firstName} ${updatedBy?.lastName}` : ''

    const formattedOwner = shipmentIssueTypeDefaultOwner
      ? `${shipmentIssueTypeDefaultOwner?.firstName} ${shipmentIssueTypeDefaultOwner?.lastName}`
      : null

    const overflowOptions = useMemo(
      () => [
        {
          itemComponent: (
            <Box display='flex'>
              <EditIcon size='large' />
              <Box ml={2} component='span'>
                Edit Issue Type
              </Box>
            </Box>
          ),
          label: 'edit',
          action: () => onEditClick('edit', id),
        },
        ...(isMpShipmentIssueAutomationEnabled
          ? [
              {
                itemComponent: (
                  <Box display='flex'>
                    <BotIcon size='large' />
                    <Box ml={2} component='span'>
                      Manage Rules for Issue Type
                    </Box>
                  </Box>
                ),
                label: 'automation',
                action: () => history.push(`/issues/${id}/automation`),
              },
            ]
          : []),
        ...(deletable
          ? [
              {
                itemComponent: (
                  <Box display='flex'>
                    <DeleteIcon size='large' />
                    <Box ml={2} component='span'>
                      Delete Issue Type
                    </Box>
                  </Box>
                ),
                label: 'delete',
                action: () => onDeleteClick(id),
              },
            ]
          : []),
      ],
      [isMpShipmentIssueAutomationEnabled, deletable, onEditClick, id, history, onDeleteClick]
    )

    return (
      <TableRow dataTest={`cp-${id}`} highlightOnHover>
        <TableCell dataTest={`cp-${id}-name`}>
          <Box fontWeight={600} display='flex' columnGap={4} alignItems='center'>
            {name || DISPLAY.empty}
            {isMpShipmentIssueAutomationEnabled && rules?.length > 0 && (
              <Tooltip trigger={<BotIcon color='coolGray5' />}>
                {rules.length} automated rules
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell dataTest={`cp-${id}-category`}>{categoryName || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`cp-${id}-description`} className={classes.descriptionCell}>
          {description || DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`cp-${id}-owner`}>{formattedOwner || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`cp-${id}-priority`}>
          <Box className={currentPriority?.className}>
            {currentPriority?.label || DISPLAY.empty}
          </Box>
        </TableCell>
        <TableCell dataTest={`cp-${id}-updated`}>
          <Typography variant='body2' component='span' display='block'>
            {formattedUpdatedAt}
          </Typography>
          <Typography variant='body2' component='span' display='block'>
            {formattedUpdatedBy}
          </Typography>
        </TableCell>
        <TableCell dataTest={`cp-${id}-options`}>
          <ActionsMenu options={overflowOptions} id={`cp-${id}-overflow`} />
        </TableCell>
      </TableRow>
    )
  }
)

export default ControlPanelRow
